import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import { getEnvPublic, isProductionEnvironment } from "@remi/environment";
import { useEffect } from "@remi/next-ui";

import { checkIsInstalledApp } from "~/utils/device";
import { appAreaFromRoute } from "~/utils/analytics/appArea";

/**
 * posthog-js only captures a pageview at init, and this app is a Pages Router
 * SPA — without this every client-side navigation is invisible, and autocaptured
 * events ($rageclick, $dead_click) carry no dimension coarser than a
 * project-id-bearing URL. Registering these as super properties puts them on
 * every event the SDK sends, including ones it captures on its own.
 */
export function usePostHogPageContext() {
  const posthog = usePostHog();
  const { asPath, pathname } = useRouter();
  // Query and hash are stripped so nuqs-backed filters don't fire a pageview per
  // keystroke. $current_url still carries the full URL, so nothing is lost but the
  // trigger. Bracketed templates mean the router has not resolved the URL yet;
  // router.isReady looks like that check but stays false on static routes forever.
  const navigationKey = asPath.split(/[?#]/)[0] ?? asPath;
  const isUnresolvedTemplate = navigationKey.includes("[");

  useEffect(() => {
    if (isUnresolvedTemplate || !(isProductionEnvironment() || getEnvPublic("NEXT_PUBLIC_POSTHOG_PREVIEW_ENABLED"))) {
      return;
    }

    posthog.register({
      appArea: appAreaFromRoute(pathname),
      route: pathname,
      displayMode: checkIsInstalledApp() ? "standalone" : "browser",
    });
    posthog.capture("$pageview");
    // navigationKey, not pathname, is what distinguishes /bids/1 from /bids/2.
  }, [navigationKey, isUnresolvedTemplate, pathname, posthog]);
}
